import React, { useState, useEffect } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import {
  Paper,
  Button,
  CardContent,
  CardActions,
  Card,
  TextField,
  Box,
  Stack,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  Grid,
  Divider,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Modal,
  CircularProgress,
  Chip,
  Alert,
  LinearProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Base64File } from '../../../utils/Base64File';
import { dataURItoBlob } from '../../../utils/Base64File';
import { KYCService, KYC_STATUS, KYC_DOCUMENT_TYPES } from '../../../utils/kycService';
import {
  validatePANNumber,
  validateAadhaarNumber,
  validateIFSCCode,
  validateBankAccountNumber,
  validateName,
  validateDateOfBirth,
  validateKYCDocument,
  fileToBase64,
  generateFilePreview,
  createFileObject,
  formatFileSize,
  handleKYCError,
  handleKYCSuccess,
  UploadProgressTracker
} from '../../../utils/kycUtils';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kyc-tabpanel-${index}`}
      aria-labelledby={`kyc-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Styled Components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StatusChip = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return 'success';
      case KYC_STATUS.PENDING:
        return 'warning';
      case KYC_STATUS.PROCESSING:
        return 'info';
      case KYC_STATUS.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={status?.toUpperCase() || 'NOT STARTED'}
      color={getStatusColor(status)}
      size="small"
      icon={
        status === KYC_STATUS.VERIFIED ? <CheckCircleIcon /> :
        status === KYC_STATUS.REJECTED ? <ErrorIcon /> :
        <WarningIcon />
      }
    />
  );
};

// Validation Schemas
const panValidationSchema = Yup.object().shape({
  pan_number: Yup.string()
    .required('PAN Number is required')
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format'),
  name: Yup.string()
    .required('Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Name must contain only alphabets and spaces'),
  date_of_birth: Yup.date()
    .required('Date of Birth is required')
    .max(new Date(), 'Date of Birth cannot be in the future')
});

const aadhaarValidationSchema = Yup.object().shape({
  aadhaar_number: Yup.string()
    .required('Aadhaar Number is required')
    .matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhaar format'),
  name: Yup.string()
    .required('Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Name must contain only alphabets and spaces')
});

const bankValidationSchema = Yup.object().shape({
  account_number: Yup.string()
    .required('Account Number is required')
    .matches(/^[0-9]{9,18}$/, 'Account number must be 9-18 digits'),
  ifsc_code: Yup.string()
    .required('IFSC Code is required')
    .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format'),
  account_holder_name: Yup.string()
    .required('Account Holder Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Name must contain only alphabets and spaces')
});

const KYCVerification = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadProgress, setUploadProgress] = useState({});
  const [kycStatus, setKycStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [uploadTracker] = useState(new UploadProgressTracker());
  const [documents, setDocuments] = useState({
    pan_document: null,
    aadhaar_front: null,
    aadhaar_back: null,
    bank_document: null
  });

  // Initialize formik for each tab
  const panFormik = useFormik({
    initialValues: {
      pan_number: '',
      name: '',
      date_of_birth: ''
    },
    validationSchema: panValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await handlePANVerification(values, setSubmitting);
    }
  });

  const aadhaarFormik = useFormik({
    initialValues: {
      aadhaar_number: '',
      name: ''
    },
    validationSchema: aadhaarValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await handleAadhaarVerification(values, setSubmitting);
    }
  });

  const bankFormik = useFormik({
    initialValues: {
      account_number: '',
      ifsc_code: '',
      account_holder_name: ''
    },
    validationSchema: bankValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await handleBankVerification(values, setSubmitting);
    }
  });

  // Load existing KYC status
  useEffect(() => {
    loadKYCStatus();
  }, []);

  // Setup upload progress tracking
  useEffect(() => {
    uploadTracker.addCallback((progress) => {
      setUploadProgress(prev => ({ ...prev, [activeTab]: progress }));
    });
  }, [activeTab, uploadTracker]);

  const loadKYCStatus = async () => {
    try {
      setIsLoading(true);
      const userId = sessionStorage.getItem('user_id');
      const response = await KYCService.getKYCStatus(userId);
      
      if (response?.data?.status) {
        setKycStatus(response.data.data);
      }
    } catch (error) {
      handleKYCError(error, 'Loading KYC status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFileUpload = async (documentType, file) => {
    try {
      const validation = validateKYCDocument(file);
      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }

      uploadTracker.startUpload();
      const base64 = await fileToBase64(file, { compress: true });
      const fileObject = createFileObject(file, base64, documentType);

      // Upload document
      const response = await KYCService.uploadDocument(fileObject);
      
      if (response?.data?.status) {
        setDocuments(prev => ({ ...prev, [documentType]: response.data.data }));
        handleKYCSuccess('Document uploaded successfully');
      }
    } catch (error) {
      handleKYCError(error, 'Document upload');
    } finally {
      uploadTracker.finishUpload();
    }
  };

  const handlePANVerification = async (values, setSubmitting) => {
    try {
      setSubmitting(true);
      const panData = {
        ...values,
        document_id: documents.pan_document?.id
      };

      const response = await KYCService.verifyPAN(panData);
      
      if (response?.data?.status) {
        handleKYCSuccess('PAN verification submitted successfully');
        setKycStatus(prev => ({ ...prev, pan: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'PAN verification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAadhaarVerification = async (values, setSubmitting) => {
    try {
      setSubmitting(true);
      const aadhaarData = {
        ...values,
        front_document_id: documents.aadhaar_front?.id,
        back_document_id: documents.aadhaar_back?.id
      };

      const response = await KYCService.verifyAadhaar(aadhaarData);
      
      if (response?.data?.status) {
        handleKYCSuccess('Aadhaar verification submitted successfully');
        setKycStatus(prev => ({ ...prev, aadhaar: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Aadhaar verification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankVerification = async (values, setSubmitting) => {
    try {
      setSubmitting(true);
      const bankData = {
        ...values,
        document_id: documents.bank_document?.id
      };

      const response = await KYCService.verifyBank(bankData);
      
      if (response?.data?.status) {
        handleKYCSuccess('Bank verification submitted successfully');
        setKycStatus(prev => ({ ...prev, bank: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Bank verification');
    } finally {
      setSubmitting(false);
    }
  };

  const getTabStatus = (tabIndex) => {
    switch (tabIndex) {
      case 0:
        return kycStatus.pan;
      case 1:
        return kycStatus.aadhaar;
      case 2:
        return kycStatus.bank;
      default:
        return null;
    }
  };

  const isTabDisabled = (tabIndex) => {
    // Disable tabs based on verification status
    const status = getTabStatus(tabIndex);
    return status === KYC_STATUS.VERIFIED || status === KYC_STATUS.PROCESSING;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" align="center" sx={{ mb: 3 }}>
        KYC Verification
      </Typography>

      {isLoading && <LinearProgress />}

      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              aria-label="KYC verification tabs"
            >
              <Tab 
                label="PAN Verification" 
                disabled={isTabDisabled(0)}
                icon={<StatusChip status={getTabStatus(0)} />}
              />
              <Tab 
                label="Aadhaar Verification" 
                disabled={isTabDisabled(1)}
                icon={<StatusChip status={getTabStatus(1)} />}
              />
              <Tab 
                label="Bank Verification" 
                disabled={isTabDisabled(2)}
                icon={<StatusChip status={getTabStatus(2)} />}
              />
            </Tabs>
          </Box>

          {/* PAN Verification Tab */}
          <TabPanel value={activeTab} index={0}>
            <FormikProvider value={panFormik}>
              <Form autoComplete="off" noValidate onSubmit={panFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="PAN Number"
                      {...panFormik.getFieldProps("pan_number")}
                      error={Boolean(panFormik.touched.pan_number && panFormik.errors.pan_number)}
                      helperText={panFormik.touched.pan_number && panFormik.errors.pan_number}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name as per PAN"
                      {...panFormik.getFieldProps("name")}
                      error={Boolean(panFormik.touched.name && panFormik.errors.name)}
                      helperText={panFormik.touched.name && panFormik.errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Date of Birth"
                      InputLabelProps={{ shrink: true }}
                      {...panFormik.getFieldProps("date_of_birth")}
                      error={Boolean(panFormik.touched.date_of_birth && panFormik.errors.date_of_birth)}
                      helperText={panFormik.touched.date_of_birth && panFormik.errors.date_of_birth}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={panFormik.isSubmitting}
                      >
                        Upload PAN Card
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload(KYC_DOCUMENT_TYPES.PAN_CARD, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      
                      {documents.pan_document && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {documents.pan_document.name} ({formatFileSize(documents.pan_document.size)})
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => window.open(documents.pan_document.url, '_blank')}
                          >
                            <LaunchIcon />
                          </IconButton>
                        </Stack>
                      )}

                      {uploadProgress[0] > 0 && uploadProgress[0] < 100 && (
                        <LinearProgress variant="determinate" value={uploadProgress[0]} />
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={panFormik.isSubmitting}
                  disabled={!documents.pan_document}
                  sx={{ mt: 2 }}
                >
                  Submit PAN Verification
                </LoadingButton>
              </Form>
            </FormikProvider>
          </TabPanel>

          {/* Aadhaar Verification Tab */}
          <TabPanel value={activeTab} index={1}>
            <FormikProvider value={aadhaarFormik}>
              <Form autoComplete="off" noValidate onSubmit={aadhaarFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Aadhaar Number"
                      {...aadhaarFormik.getFieldProps("aadhaar_number")}
                      error={Boolean(aadhaarFormik.touched.aadhaar_number && aadhaarFormik.errors.aadhaar_number)}
                      helperText={aadhaarFormik.touched.aadhaar_number && aadhaarFormik.errors.aadhaar_number}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name as per Aadhaar"
                      {...aadhaarFormik.getFieldProps("name")}
                      error={Boolean(aadhaarFormik.touched.name && aadhaarFormik.errors.name)}
                      helperText={aadhaarFormik.touched.name && aadhaarFormik.errors.name}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={aadhaarFormik.isSubmitting}
                      >
                        Upload Aadhaar Front
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload(KYC_DOCUMENT_TYPES.AADHAAR_FRONT, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      
                      {documents.aadhaar_front && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {documents.aadhaar_front.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => window.open(documents.aadhaar_front.url, '_blank')}
                          >
                            <LaunchIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={aadhaarFormik.isSubmitting}
                      >
                        Upload Aadhaar Back
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload(KYC_DOCUMENT_TYPES.AADHAAR_BACK, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      
                      {documents.aadhaar_back && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {documents.aadhaar_back.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => window.open(documents.aadhaar_back.url, '_blank')}
                          >
                            <LaunchIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={aadhaarFormik.isSubmitting}
                  disabled={!documents.aadhaar_front || !documents.aadhaar_back}
                  sx={{ mt: 2 }}
                >
                  Submit Aadhaar Verification
                </LoadingButton>
              </Form>
            </FormikProvider>
          </TabPanel>

          {/* Bank Verification Tab */}
          <TabPanel value={activeTab} index={2}>
            <FormikProvider value={bankFormik}>
              <Form autoComplete="off" noValidate onSubmit={bankFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      {...bankFormik.getFieldProps("account_number")}
                      error={Boolean(bankFormik.touched.account_number && bankFormik.errors.account_number)}
                      helperText={bankFormik.touched.account_number && bankFormik.errors.account_number}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      {...bankFormik.getFieldProps("ifsc_code")}
                      error={Boolean(bankFormik.touched.ifsc_code && bankFormik.errors.ifsc_code)}
                      helperText={bankFormik.touched.ifsc_code && bankFormik.errors.ifsc_code}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Holder Name"
                      {...bankFormik.getFieldProps("account_holder_name")}
                      error={Boolean(bankFormik.touched.account_holder_name && bankFormik.errors.account_holder_name)}
                      helperText={bankFormik.touched.account_holder_name && bankFormik.errors.account_holder_name}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={bankFormik.isSubmitting}
                      >
                        Upload Bank Document
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload(KYC_DOCUMENT_TYPES.BANK_PASSBOOK, e.target.files[0]);
                            }
                          }}
                        />
                      </Button>
                      
                      {documents.bank_document && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {documents.bank_document.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => window.open(documents.bank_document.url, '_blank')}
                          >
                            <LaunchIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={bankFormik.isSubmitting}
                  disabled={!documents.bank_document}
                  sx={{ mt: 2 }}
                >
                  Submit Bank Verification
                </LoadingButton>
              </Form>
            </FormikProvider>
          </TabPanel>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={loadKYCStatus}
              disabled={isLoading}
            >
              Refresh Status
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={Object.values(kycStatus).some(status => status !== KYC_STATUS.VERIFIED)}
            >
              All Verified
            </Button>
          </Stack>
        </CardActions>
      </Card>
    </Box>
  );
};

export default KYCVerification;
